"use client";

import { useState } from "react";
import {
    ArrowRight,
    BookOpen,
    CheckCircle2,
    Database,
    ExternalLink,
    Loader2,
    Sparkles,
    TriangleAlert,
} from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Checkbox } from "~/components/ui/checkbox";
import { ClientInstructions } from "./client-instructions";
import { ClientSegmentedControl } from "../ClientSegmentedControl";
import { mcpConfig } from "../../lib/mcp-config";
import {
    DEFAULT_MCP_CLIENT,
    getMcpClient,
    type McpClientId,
    type McpCredential,
} from "../../lib/mcp-clients";
import * as Analytics from "../../analytics";
import { Routes } from "~/routes";
import { Link } from "~/components/Link";

// // // //

const MIN_NAME_LENGTH = 10;

export function McpWizard(props: {
    generate: (name: string, client: McpClientId) => Promise<McpCredential>;
    onComplete?: () => void;
    initialClient?: McpClientId;
}) {
    const { generate, onComplete, initialClient } = props;

    const [step, setStep] = useState(0);
    // Start on the client chosen where the wizard was opened (e.g. the setup
    // guide); the user can still switch clients in the first step.
    const [selectedClient, setSelectedClient] = useState<McpClientId>(
        initialClient ?? DEFAULT_MCP_CLIENT,
    );
    const [name, setName] = useState("");
    const [loading, setLoading] = useState(false);
    const [credential, setCredential] = useState<McpCredential | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [closed, setClosed] = useState(false);

    function handleSelectClient(client: McpClientId) {
        Analytics.trackSelectClient({ location: "wizard", client });
        setSelectedClient(client);
    }

    async function handleCreate() {
        const trimmed = name.trim();
        if (trimmed.length < MIN_NAME_LENGTH) {
            setError(
                `Connection name must be at least ${MIN_NAME_LENGTH} characters.`,
            );
            return;
        }
        setError(null);
        setLoading(true);
        try {
            const result = await generate(trimmed, selectedClient);
            setCredential(result);
            setStep(1);
        } catch {
            setError(
                "Something went wrong creating your connection. Please try again.",
            );
        } finally {
            setLoading(false);
        }
    }

    if (closed) {
        return (
            <div className="w-full max-w-xl text-center">
                <p className="text-pretty text-lg font-medium text-foreground">
                    {mcpConfig.serverName} is connected.
                </p>
                <p className="mt-2 text-sm text-muted-foreground">
                    You can close this window.
                </p>
            </div>
        );
    }

    return (
        <div className="w-full">
            <div className="mt-6 rounded-xl border bg-card p-6 shadow-sm sm:p-8">
                {step === 0 ? (
                    <ClientAndNameStep
                        selectedClient={selectedClient}
                        onSelectClient={handleSelectClient}
                        name={name}
                        setName={setName}
                        loading={loading}
                        error={error}
                        clearError={() => setError(null)}
                        onSubmit={handleCreate}
                    />
                ) : step === 1 && credential ? (
                    <ConnectStep
                        client={selectedClient}
                        credential={credential}
                        onDone={() => setStep(2)}
                    />
                ) : step === 2 ? (
                    <DoneStep
                        client={selectedClient}
                        onClose={() => {
                            if (onComplete) {
                                onComplete();
                                return;
                            }
                            setClosed(true);
                        }}
                    />
                ) : null}
            </div>
        </div>
    );
}

function ClientAndNameStep({
    selectedClient,
    onSelectClient,
    name,
    setName,
    loading,
    error,
    clearError,
    onSubmit,
}: {
    selectedClient: McpClientId;
    onSelectClient: (client: McpClientId) => void;
    name: string;
    setName: (v: string) => void;
    loading: boolean;
    error: string | null;
    clearError: () => void;
    onSubmit: () => void;
}) {
    const tooShort = name.trim().length < MIN_NAME_LENGTH;
    return (
        <form
            onSubmit={(e) => {
                e.preventDefault();
                onSubmit();
            }}
        >
            <div className="flex items-center gap-2 text-primary">
                <Sparkles className="size-5" />
                <span className="text-xs font-semibold uppercase tracking-wide">
                    Connect {mcpConfig.serverName}
                </span>
            </div>
            <h1 className="mt-3 text-pretty text-2xl font-semibold tracking-tight text-foreground">
                Set up a new connection
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                Choose the app you&apos;re connecting from and give the
                connection a name so you can recognize it later.
            </p>

            <div className="mt-6 flex flex-col gap-2">
                <Label>MCP client</Label>
                <ClientSegmentedControl
                    value={selectedClient}
                    onValueChange={onSelectClient}
                />
            </div>

            <div className="mt-4 flex flex-col gap-2">
                <Label htmlFor="client-name">Connection name</Label>
                <Input
                    id="client-name"
                    autoFocus
                    placeholder="e.g. My MacBook"
                    value={name}
                    onChange={(e) => {
                        setName(e.target.value);
                        if (error) clearError();
                    }}
                    minLength={MIN_NAME_LENGTH}
                    maxLength={60}
                    aria-invalid={!!error}
                    aria-describedby={error ? "client-name-error" : undefined}
                />
                {error ? (
                    <p
                        id="client-name-error"
                        className="text-sm text-destructive"
                    >
                        {error}
                    </p>
                ) : (
                    <p className="text-xs text-muted-foreground">
                        Must be at least {MIN_NAME_LENGTH} characters.
                    </p>
                )}
            </div>

            <Button
                type="submit"
                className="mt-6 w-full"
                variant="primaryLight"
                disabled={tooShort || loading}
            >
                {loading ? (
                    <>
                        <Loader2 className="size-4 animate-spin" />
                        Creating your connection…
                    </>
                ) : (
                    <>
                        Create &amp; get credentials
                        <ArrowRight className="size-4" />
                    </>
                )}
            </Button>
        </form>
    );
}

function connectSubhead(client: McpClientId): string {
    switch (client) {
        case "claude":
            return "Add the connector in Claude using the details below to finish setup.";
        case "claude-code":
            return "Run the command below in your terminal to finish setup.";
        case "codex-desktop":
            return "Add the connector in Codex using the details below to finish setup.";
        case "cursor":
            return "Paste the configuration below into Cursor to finish setup.";
    }
}

function ConnectStep({
    client,
    credential,
    onDone,
}: {
    client: McpClientId;
    credential: McpCredential;
    onDone: () => void;
}) {
    const [confirmed, setConfirmed] = useState(false);
    const isOauth = credential.kind === "oauth";

    return (
        <div>
            <div className="flex items-center gap-2 text-primary">
                <Sparkles className="size-5" />
                <span className="text-xs font-semibold uppercase tracking-wide">
                    Final step
                </span>
            </div>
            <h1 className="mt-3 text-pretty text-2xl font-semibold tracking-tight text-foreground">
                Connect {mcpConfig.serverName}
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                {connectSubhead(client)}
            </p>

            <div className="mt-6">
                <ClientInstructions client={client} credential={credential} />
            </div>

            {isOauth ? (
                <>
                    {/* One-time secret warning — OAuth secrets are never shown again. */}
                    <div className="mt-4 flex items-start gap-2 rounded-md border bg-muted px-3 py-2.5">
                        <TriangleAlert className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                        <p className="text-xs leading-relaxed text-muted-foreground">
                            The client secret is shown only once. Copy it now
                            and store it somewhere safe — you won&apos;t be able
                            to view it again.
                        </p>
                    </div>

                    <label
                        htmlFor="confirm-secret"
                        className="mt-4 flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors hover:bg-muted/50"
                    >
                        <Checkbox
                            id="confirm-secret"
                            checked={confirmed}
                            onCheckedChange={(c) => setConfirmed(c === true)}
                            className="mt-0.5"
                        />
                        <span className="text-sm leading-relaxed text-foreground">
                            I&apos;ve copied my client secret and stored it
                            somewhere safe.
                        </span>
                    </label>
                </>
            ) : null}

            <Button
                type="button"
                className="mt-6 w-full"
                onClick={onDone}
                disabled={isOauth && !confirmed}
            >
                {isOauth ? "I've added the connector" : "I'm done"}
                <ArrowRight className="size-4" />
            </Button>
        </div>
    );
}

function DoneStep({
    client,
    onClose,
}: {
    client: McpClientId;
    onClose: () => void;
}) {
    const isOauth = getMcpClient(client).auth === "oauth";
    const settingsHref = isOauth ? Routes.oauthClients : Routes.apiToolsApiKeys;
    const settingsLabel = isOauth ? "OAuth clients and tokens" : "API keys";
    const settingsLinkText = isOauth ? "Settings page" : "API Keys page";

    return (
        <div className="flex flex-col items-center text-center">
            <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
                <CheckCircle2 className="size-8" />
            </div>
            <h1 className="mt-5 text-pretty text-2xl font-semibold tracking-tight text-foreground">
                We&apos;re all done.
            </h1>
            <p className="mt-2 max-w-sm text-sm leading-relaxed text-muted-foreground">
                You may now use the {mcpConfig.serverName} MCP server to query
                data.
            </p>

            {/* Quick links */}
            <div className="mt-6 grid w-full gap-3 sm:grid-cols-2">
                <LinkTile
                    href={mcpConfig.dataCatalogUrl}
                    icon={<Database className="size-5" />}
                    title="Data Catalog"
                    description="Browse available datasets"
                />
                <LinkTile
                    href={mcpConfig.docsUrl}
                    icon={<BookOpen className="size-5" />}
                    title="API Docs"
                    description="Read the documentation"
                />
            </div>

            <p className="mt-6 text-sm leading-relaxed text-muted-foreground">
                You can manage your {settingsLabel} anytime on the{" "}
                <Link
                    href={settingsHref}
                    analyticsLabel="McpWizard - Settings page"
                    className="whitespace-nowrap font-medium text-link underline-offset-4 hover:underline"
                >
                    {settingsLinkText}
                </Link>
                .
            </p>

            <Button
                type="button"
                variant="outline"
                className="mt-6 w-full"
                onClick={onClose}
            >
                Close
            </Button>
        </div>
    );
}

function LinkTile({
    href,
    icon,
    title,
    description,
}: {
    href: string;
    icon: React.ReactNode;
    title: string;
    description: string;
}) {
    return (
        <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="group flex items-center gap-3 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary hover:bg-muted/50"
        >
            <span className="flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-foreground transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                {icon}
            </span>
            <span className="min-w-0">
                <span className="flex items-center gap-1 text-sm font-medium text-foreground">
                    {title}
                    <ExternalLink className="size-3.5 text-muted-foreground" />
                </span>
                <span className="block truncate text-xs text-muted-foreground">
                    {description}
                </span>
            </span>
        </a>
    );
}
